/**
 * Strength Projection Module - PSP/CRE Tool v3.0
 * 
 * @module strengthProjectionModule
 * @version 2.2.0
 * @date February 24, 2026
 *
 * VERSION 2.2.0 - SPM-02/06 constants migration (February 24, 2026)
 * - SPM-02: Local STRENGTH_EFFECTIVENESS deleted. Migrated to operationalStates.js v2.2.
 *   Re-exported as const alias for backward compatibility.
 * - SPM-06: Local PERSONNEL_CATEGORIES deleted. Migrated to operationalStates.js v2.2.
 *   Re-exported as const alias for backward compatibility.
 * @version 2.1.0
 * @date February 24, 2026
 * VERSION 2.1.0 - SPM-03/04/05 constants migration (February 24, 2026)
 * - SPM-03: Local REPLACEMENT_THRESHOLDS deleted → imported+re-exported from theatreHoldingPolicy.js v2.4
 * - SPM-04: Local REPLACEMENT_LEAD_TIMES deleted → imported+re-exported from theatreHoldingPolicy.js v2.4
 * - SPM-05: Local RTD_RATES deleted → imported from theatreHoldingPolicy.js (BY_TRIAGE_CATEGORY structure)
 *           Local DNBI_RATE=0.60 deleted → DNBI_RTD_RATE from theatreHoldingPolicy.js v2.4
 *           calculateRTDReturns() rewritten: T3@72h=0.40 (was 0.30), T2@7d=0.20 (unchanged)
 *           DNBI distributed over days 3-7 (unchanged). THP values are doctrinal authority.
 * @version 2.0.0
 * @date February 22, 2026
 * @author Dr. Tatar Radu MD / Claude AI
 *
 * VERSION 2.0.0 - Phase 3-B partial constants migration (February 22, 2026)
 * - SPM-01 (partial): Added imports:
 *     PERMANENT_LOSS_RATE from theatreHoldingPolicy.js
 *     getDefaultStrength from nationalStandards.js
 * - SPM-07: calculatePermanentLosses() — replaced `wia * 0.60` literal with
 *   `wia * PERMANENT_LOSS_RATE` (APP_RULES 2.1 single source of truth).
 * - SPM-08: calculateStrengthProjection() unit initial strength — replaced
 *   `|| 4000` literal fallback with getDefaultStrength(nationalStandard, echelon)
 *   guard. If strength not in unit config and getDefaultStrength returns 0,
 *   throws descriptive error (APP_RULES 6.3: no silent defaults).
 *
 * DEFERRED items (target constants not yet in canonical form):
 * - SPM-02: ✅ DONE in v2.2.0 — STRENGTH_EFFECTIVENESS migrated to operationalStates.js v2.2, re-exported
 * - SPM-03: REPLACEMENT_THRESHOLDS — not in theatreHoldingPolicy.js (EXT-05 scope)
 * - SPM-04: REPLACEMENT_LEAD_TIMES — not in theatreHoldingPolicy.js
 * - SPM-05: ✅ DONE in v2.1.0 — see VERSION 2.1.0 above.
 * - SPM-06: ✅ DONE in v2.2.0 — PERSONNEL_CATEGORIES migrated to operationalStates.js v2.2, re-exported
 * 
 * @description
 * Projects unit strength over operation duration, tracks combat effectiveness,
 * generates threshold warnings when units fall below 70%, and creates G1 
 * personnel replacement requests.
 * 
 * Issue ID: CC-005
 * Priority: HIGH
 * 
 * Doctrinal Reference:
 * - FM 12-6: Personnel Doctrine - Casualty Operations
 * - ATP 4-02.55: AHS Support Planning
 * - AJMedP-1: Allied Joint Medical Planning Doctrine
 */

// ============================================================================
// IMPORTS
// SPM-01 (partial): Import canonical constants available in Phase 3.
// SPM-03/04/05/06 imports deferred — see version changelog above.
// ============================================================================

// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';

import {
  PERMANENT_LOSS_RATE,
  RTD_RATES,             // SPM-05: canonical RTD rates by triage category
  REPLACEMENT_THRESHOLDS, // SPM-03: migrated from local block
  REPLACEMENT_LEAD_TIMES, // SPM-04: migrated from local block
  DNBI_RTD_RATE          // SPM-05: DNBI return-to-duty rate
} from '../../constants/theatreHoldingPolicy';
import { getDefaultStrength } from '../../constants/nationalStandards';

// SPM-02/06: STRENGTH_EFFECTIVENESS + PERSONNEL_CATEGORIES from operationalStates.js v2.2
import {
  STRENGTH_EFFECTIVENESS as STRENGTH_EFFECTIVENESS_CANONICAL,
  PERSONNEL_CATEGORIES as PERSONNEL_CATEGORIES_CANONICAL
} from '../../constants/operationalStates';

// ============================================================================
// CONSTANTS (inline — partial migration, see deferred items in changelog)
// ============================================================================

// SPM-02 FIX (February 24, 2026): Local STRENGTH_EFFECTIVENESS deleted.
// Migrated to operationalStates.js v2.2. Re-exported for backward compatibility.
// Consumers that import STRENGTH_EFFECTIVENESS from this module continue to work.
export const STRENGTH_EFFECTIVENESS = STRENGTH_EFFECTIVENESS_CANONICAL;

/**
 * SPM-03 FIX: Local REPLACEMENT_THRESHOLDS deleted.
 * Imported from theatreHoldingPolicy.js v2.4. Re-exported for backward compatibility.
 */
export { REPLACEMENT_THRESHOLDS };

/**
 * SPM-04 FIX: Local REPLACEMENT_LEAD_TIMES deleted.
 * Imported from theatreHoldingPolicy.js v2.4. Re-exported for backward compatibility.
 */
export { REPLACEMENT_LEAD_TIMES };

/**
 * SPM-05 FIX: Local RTD_RATES deleted. Imported RTD_RATES from
 * theatreHoldingPolicy.js (BY_TRIAGE_CATEGORY structure). DNBI_RTD_RATE
 * also from theatreHoldingPolicy.js v2.4. calculateRTDReturns() rewritten
 * to use canonical rate keys (within_72h, within_7d). (February 24, 2026)
 * Value change: T3 at 72h = 0.40 (was split 0.30+0.50=0.80 cumulative).
 * THP values are authoritative (doctrinal research basis).
 */

// SPM-06 FIX (February 24, 2026): Local PERSONNEL_CATEGORIES deleted.
// Migrated to operationalStates.js v2.2. Re-exported for backward compatibility.
export const PERSONNEL_CATEGORIES = PERSONNEL_CATEGORIES_CANONICAL;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get effectiveness rating based on strength percentage
 * @param {number} strengthPercent - Current strength as percentage (0-100)
 * @returns {Object} Effectiveness rating object
 */
export const getEffectivenessRating = (strengthPercent) => {
  if (strengthPercent >= STRENGTH_EFFECTIVENESS.FULL_EFFECTIVE.min) {
    return STRENGTH_EFFECTIVENESS.FULL_EFFECTIVE;
  }
  if (strengthPercent >= STRENGTH_EFFECTIVENESS.REDUCED_CAPABILITY.min) {
    return STRENGTH_EFFECTIVENESS.REDUCED_CAPABILITY;
  }
  if (strengthPercent >= STRENGTH_EFFECTIVENESS.MISSION_DEGRADED.min) {
    return STRENGTH_EFFECTIVENESS.MISSION_DEGRADED;
  }
  if (strengthPercent >= STRENGTH_EFFECTIVENESS.COMBAT_INEFFECTIVE.min) {
    return STRENGTH_EFFECTIVENESS.COMBAT_INEFFECTIVE;
  }
  return STRENGTH_EFFECTIVENESS.DESTROYED;
};

/**
 * Calculate days until unit reaches threshold
 * @param {number} currentPercent - Current strength percentage
 * @param {number} dailyLossRate - Average daily loss rate (percentage points)
 * @param {number} threshold - Target threshold percentage
 * @returns {number} Days until threshold reached, or Infinity if won't reach
 */
export const calculateDaysToThreshold = (currentPercent, dailyLossRate, threshold) => {
  if (dailyLossRate <= 0) return Infinity;
  if (currentPercent <= threshold) return 0;
  
  return Math.ceil((currentPercent - threshold) / dailyLossRate);
};

/**
 * Determine request priority based on days to threshold
 * @param {number} daysToThreshold - Days until threshold breach
 * @param {number} leadTime - Lead time for replacement arrival
 * @returns {string} Priority level: 'ROUTINE', 'PRIORITY', or 'EMERGENCY'
 */
const determineRequestPriority = (daysToThreshold, leadTime) => {
  const buffer = daysToThreshold - leadTime;
  
  if (buffer <= 0) return 'EMERGENCY';
  if (buffer <= 2) return 'PRIORITY';
  return 'ROUTINE';
};

/**
 * Calculate permanent losses from casualty data
 * @param {Object} casualties - Daily casualty breakdown
 * @returns {number} Total permanent losses
 * SPM-07: `wia * 0.60` replaced with `wia * PERMANENT_LOSS_RATE` (APP_RULES 2.1).
 */
const calculatePermanentLosses = (casualties) => {
  const kia = casualties?.kia || casualties?.KIA || 0;
  const dow = casualties?.dow || casualties?.DOW || 0;
  const wia = casualties?.wia || casualties?.WIA || 0;

  // KIA + DOW + PERMANENT_LOSS_RATE of WIA (those evacuated beyond unit)
  return kia + dow + Math.round(wia * PERMANENT_LOSS_RATE);
};

/**
 * Calculate RTD returns for a given day
 * @param {Array} casualtyHistory - Previous days' casualties
 * @param {number} currentDay - Current day number
 * @returns {number} RTD returns for this day
 */
const calculateRTDReturns = (casualtyHistory, currentDay) => {
  // SPM-05 FIX: Rewritten to use RTD_RATES.BY_TRIAGE_CATEGORY from
  // theatreHoldingPolicy.js (February 24, 2026). Rates are cumulative.
  // Incremental rates derived: T3@72h=0.40, T3@7d incremental=0.30 (0.70-0.40),
  // T2@7d=0.20. DNBI uses DNBI_RTD_RATE=0.60 from theatreHoldingPolicy.js.
  // Doctrinal correction: prior T3 cumulative-by-day3 was 0.80 (too high);
  // canonical THP value is 0.40 (within_72h), validated against NATO doctrine.
  const t3 = RTD_RATES.BY_TRIAGE_CATEGORY.T3_MINIMAL.rtd_rates;
  const t2 = RTD_RATES.BY_TRIAGE_CATEGORY.T2_DELAYED.rtd_rates;

  let returns = 0;

  casualtyHistory.forEach((dayCas, index) => {
    const dayNumber = index + 1;
    const daysSince = currentDay - dayNumber;

    if (daysSince > 0) {
      const t3Count  = dayCas?.t3 || dayCas?.triage_3 || 0;
      const t2Count  = dayCas?.t2 || dayCas?.triage_2 || 0;
      const dnbiCount = dayCas?.dnbi || 0;

      // T3 returns: 40% cumulative by 72h (day 3), additional 30% by day 7
      if (daysSince === 3) {
        returns += Math.round(t3Count * t3.within_72h);           // 0.40
      } else if (daysSince === 7) {
        // Incremental: within_7d (0.70) minus already-returned within_72h (0.40)
        const t3Incremental = Math.max(0, t3.within_7d - t3.within_72h); // 0.30
        returns += Math.round(t3Count * t3Incremental);
        // T2 returns: 20% cumulative by 7 days
        returns += Math.round(t2Count * t2.within_7d);             // 0.20
      }

      // DNBI returns: distributed over days 3-7 (5 days)
      if (daysSince >= 3 && daysSince <= 7) {
        returns += Math.round((dnbiCount * DNBI_RTD_RATE) / 5);
      }
    }
  });

  return returns;
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate unit strength projection throughout operation
 * 
 * @param {Object} scenario - Full scenario object containing:
 *   - force.units: Array of unit objects with strength data
 *   - logistics.personnel_arrivals: Planned replacement arrivals
 *   - operation_parameters.duration_days: Operation length
 * @param {Array} dailyCasualties - Daily casualty breakdown per unit
 *   Each element: { day, unit_id, kia, dow, wia, t1, t2, t3, dnbi, ... }
 * @param {Array} [rtdReturns] - RTD returns per day (optional, will calculate if not provided)
 * 
 * @returns {Object} Strength projection analysis:
 *   - by_unit: Array of per-unit strength curves
 *   - threshold_warnings: Units crossing 70% threshold
 *   - g1_requests: G1 personnel request recommendations
 *   - replacement_trigger_threshold: Applied threshold (70%)
 *   - corps_summary: Aggregate corps strength projection
 */
export const calculateStrengthProjection = (scenario, dailyCasualties, rtdReturns = null) => {
  // Validate inputs
  if (!scenario) {
    console.error('[StrengthProjection] No scenario provided');
    return createEmptyResult();
  }
  
  // Extract parameters
  const units = scenario.force?.units || 
                scenario.force?.forceBuilderResult?.corps?.divisions?.flatMap(d => 
                  d.brigades?.flatMap(b => b.battalions || []) || []
                ) || [];
  
  const personnelArrivals = scenario.logistics?.personnel_arrivals || [];
  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const threshold = REPLACEMENT_THRESHOLDS.TRIGGER / 100; // Convert to decimal
  
  // Result arrays
  const byUnit = [];
  const thresholdWarnings = [];
  const g1Requests = [];
  let corpsTotalInitial = 0;
  const corpsDailyStrength = [];
  
  // Initialize corps daily tracking
  for (let day = 1; day <= duration; day++) {
    corpsDailyStrength.push({ day, strength: 0, percent: 100 });
  }
  
  // Process each unit
  units.forEach(unit => {
    const unitId = unit.id || unit.unit_id;
    const unitName = unit.name || unit.unit_name || `Unit ${unitId}`;

    // SPM-08: || 4000 replaced with getDefaultStrength() — no silent defaults (APP_RULES 6.3)
    let initial = unit.strength || unit.initial_strength || unit.personnel;
    if (!initial) {
      const ns = scenario.national_standard || scenario.force?.national_standard || 'NATO';
      const echelon = unit.echelon || unit.type || unit.unit_type;
      if (!echelon) {
        throw new Error(
          `[StrengthProjection] Unit "${unitName}" has no strength configured and no echelon to derive default`
        );
      }
      initial = getDefaultStrength(ns, echelon);
      if (!initial) {
        throw new Error(
          `[StrengthProjection] Unit "${unitName}": getDefaultStrength("${ns}", "${echelon}") returned ${initial} — check nationalStandards.js config`
        );
      }
    }
    
    corpsTotalInitial += initial;
    
    // Filter casualties for this unit
    const unitCasualties = dailyCasualties.filter(c => 
      (c.unit_id === unitId) || (c.unit_name === unitName)
    );
    
    // Filter arrivals for this unit
    const unitArrivals = personnelArrivals.filter(a => 
      a.destination_unit === unitId || a.destination_unit === unitName
    );
    
    let currentStrength = initial;
    const daily = [];
    let belowThreshold = false;
    let thresholdDay = null;
    const casualtyHistory = [];
    
    // Process each day
    for (let day = 1; day <= duration; day++) {
      // Get casualties for this day
      const dayCas = unitCasualties.find(c => c.day === day) || {};
      casualtyHistory.push(dayCas);
      
      // Subtract permanent losses (KIA + DOW + evacuated WIA)
      const permanentLoss = calculatePermanentLosses(dayCas);
      currentStrength -= permanentLoss;
      
      // Add RTD returns (either provided or calculated)
      const dayRtd = rtdReturns?.find(r => r.day === day && (r.unit_id === unitId || r.unit_name === unitName));
      if (dayRtd) {
        currentStrength += dayRtd.returns || 0;
      } else {
        // Calculate RTD if not provided
        currentStrength += calculateRTDReturns(casualtyHistory, day);
      }
      
      // Add personnel arrivals for this day
      const dayArrivals = unitArrivals.filter(a => a.day === day);
      const arrivalsTotal = dayArrivals.reduce((sum, arr) => sum + (arr.quantity || 0), 0);
      currentStrength += arrivalsTotal;
      
      // Ensure non-negative
      currentStrength = Math.max(0, currentStrength);
      
      const percent = Math.round((currentStrength / initial) * 100);
      const effectiveness = getEffectivenessRating(percent);
      
      // Check threshold crossing (first time below 70%)
      if (!belowThreshold && percent < threshold * 100) {
        belowThreshold = true;
        thresholdDay = day;
        
        thresholdWarnings.push({
          unit_id: unitId,
          unit_name: unitName,
          initial_strength: initial,
          threshold: threshold * 100,
          crosses_on_day: day,
          projected_percent: percent,
          projected_strength: currentStrength,
          effectiveness: effectiveness.status
        });
      }
      
      // Record daily strength
      daily.push({
        day,
        strength: currentStrength,
        percent,
        effectiveness: effectiveness.status,
        color: effectiveness.color
      });
      
      // Aggregate to corps level
      corpsDailyStrength[day - 1].strength += currentStrength;
    }
    
    // Find minimum strength point
    const minDay = daily.reduce((min, d) => d.percent < min.percent ? d : min, daily[0] || { percent: 100 });
    
    // Calculate average daily loss rate
    const totalLoss = initial - (daily[daily.length - 1]?.strength || initial);
    const avgDailyLossRate = duration > 0 ? (totalLoss / initial * 100) / duration : 0;
    
    byUnit.push({
      unit_id: unitId,
      unit_name: unitName,
      initial,
      daily,
      min_strength: minDay.strength,
      min_percent: minDay.percent,
      min_day: minDay.day,
      below_threshold: belowThreshold,
      threshold_day: thresholdDay,
      avg_daily_loss_rate: Math.round(avgDailyLossRate * 100) / 100,
      final_strength: daily[daily.length - 1]?.strength || initial,
      final_percent: daily[daily.length - 1]?.percent || 100,
      effectiveness: getEffectivenessRating(daily[daily.length - 1]?.percent || 100)
    });
    
    // Generate G1 request if unit crosses threshold
    if (belowThreshold) {
      const leadTime = REPLACEMENT_LEAD_TIMES.COMBAT.days;
      const requestDay = Math.max(1, thresholdDay - leadTime);
      const neededQty = Math.ceil(initial * threshold - minDay.strength);
      const priority = determineRequestPriority(thresholdDay - 1, leadTime);
      
      g1Requests.push({
        request_by_day: requestDay,
        unit_name: unitName,
        unit_id: unitId,
        personnel_type: 'COMBAT',
        quantity_needed: neededQty,
        needed_by_day: thresholdDay,
        priority,
        current_percent: Math.round((daily[requestDay - 1]?.percent || 100)),
        reason: `Unit projected to reach ${Math.round(threshold * 100)}% strength on Day ${thresholdDay}`,
        lead_time_days: leadTime
      });
    }
  });
  
  // Calculate corps percentages
  corpsDailyStrength.forEach(dayData => {
    dayData.percent = corpsTotalInitial > 0 
      ? Math.round((dayData.strength / corpsTotalInitial) * 100)
      : 100;
  });
  
  // Sort G1 requests by request day (earliest first)
  g1Requests.sort((a, b) => a.request_by_day - b.request_by_day);
  
  // Sort threshold warnings by day
  thresholdWarnings.sort((a, b) => a.crosses_on_day - b.crosses_on_day);
  
  return {
    by_unit: byUnit,
    threshold_warnings: thresholdWarnings,
    g1_requests: g1Requests,
    replacement_trigger_threshold: threshold * 100,
    corps_summary: {
      initial_strength: corpsTotalInitial,
      daily: corpsDailyStrength,
      final_strength: corpsDailyStrength[corpsDailyStrength.length - 1]?.strength || corpsTotalInitial,
      final_percent: corpsDailyStrength[corpsDailyStrength.length - 1]?.percent || 100,
      units_below_threshold: thresholdWarnings.length,
      total_units: byUnit.length
    }
  };
};

/**
 * Create empty result structure
 * @returns {Object} Empty strength projection result
 */
const createEmptyResult = () => ({
  by_unit: [],
  threshold_warnings: [],
  g1_requests: [],
  replacement_trigger_threshold: 70,
  corps_summary: {
    initial_strength: 0,
    daily: [],
    final_strength: 0,
    final_percent: 100,
    units_below_threshold: 0,
    total_units: 0
  }
});

// ============================================================================
// UTILITY FUNCTIONS FOR EXTERNAL USE
// ============================================================================

/**
 * Get status color for UI display
 * @param {number} strengthPercent - Current strength percentage
 * @returns {string} MUI color string
 */
export const getStrengthStatusColor = (strengthPercent) => {
  return getEffectivenessRating(strengthPercent).color;
};

/**
 * Format G1 request for display/export
 * @param {Object} request - G1 request object
 * @returns {Object} Formatted request
 */
export const formatG1Request = (request) => {
  return {
    priority: request.priority,
    submit_by: `Day ${request.request_by_day}`,
    unit: request.unit_name,
    type: request.personnel_type.replace(/_/g, ' '),
    quantity: request.quantity_needed,
    needed_by: `Day ${request.needed_by_day}`,
    current_percent: `${request.current_percent}%`,
    reason: request.reason
  };
};

/**
 * Check if unit needs immediate attention
 * @param {number} strengthPercent - Current strength percentage
 * @returns {boolean} True if critical attention needed
 */
export const needsImmediateAttention = (strengthPercent) => {
  return strengthPercent <= REPLACEMENT_THRESHOLDS.CRITICAL;
};

/**
 * Calculate replacement quantity needed to restore unit
 * @param {number} currentStrength - Current unit strength
 * @param {number} initialStrength - Original unit strength
 * @param {number} targetPercent - Target percentage (default 85%)
 * @returns {number} Number of replacements needed
 */
export const calculateReplacementsNeeded = (currentStrength, initialStrength, targetPercent = 85) => {
  const targetStrength = Math.ceil(initialStrength * (targetPercent / 100));
  return Math.max(0, targetStrength - currentStrength);
};

/**
 * Get projected strength for a unit on a specific day
 * @param {Object} unitProjection - Unit projection from by_unit array
 * @param {number} day - Day number (1-based)
 * @returns {Object} Day's strength data or null
 */
export const getUnitStrengthOnDay = (unitProjection, day) => {
  return unitProjection?.daily?.find(d => d.day === day) || null;
};

// ============================================================================
// MODULE EXPORTS
// ============================================================================

const StrengthProjectionModule = {
  calculateStrengthProjection,
  getEffectivenessRating,
  calculateDaysToThreshold,
  formatG1Request,
  needsImmediateAttention,
  calculateReplacementsNeeded,
  getUnitStrengthOnDay,
  getStrengthStatusColor,
  STRENGTH_EFFECTIVENESS,
  REPLACEMENT_THRESHOLDS,
  REPLACEMENT_LEAD_TIMES,
  RTD_RATES,
  PERSONNEL_CATEGORIES
};

export default StrengthProjectionModule;
